import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../config/api';

const AIAssistantModal = ({ isOpen, onClose, projectId, prefillTitle, prefillDescription, onProtocolGenerated }) => {
  const [projectName, setProjectName] = useState('Unknown Project');
  const [userInput, setUserInput] = useState(prefillTitle || '');
  const [documentFile, setDocumentFile] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showProtocolForm, setShowProtocolForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [loadingSubtext, setLoadingSubtext] = useState('This may take a minute.');
  
  // Form data
  const [formData, setFormData] = useState({
    draft_title: prefillTitle || '',
    protocol_description: prefillDescription || ''
  });
  const [steps, setSteps] = useState([]);
  const [reagents, setReagents] = useState([]);

  const fileInputRef = useRef(null);
  const fileInfoRef = useRef(null);

  const fetchProjectInfo = useCallback(async () => {
    if (projectId && projectId !== '0') {
      try {
        const response = await API.get(`/qurator/projects/${projectId}`);
        if (response.data.success) {
          setProjectName(response.data.project?.project_name || 'Unknown Project');
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      fetchProjectInfo();
      if (prefillTitle) {
        setUserInput(prefillTitle);
        setFormData(prev => ({ ...prev, draft_title: prefillTitle }));
      }
      if (prefillDescription) {
        setFormData(prev => ({ ...prev, protocol_description: prefillDescription }));
      }
    } else {
      // Reset when modal closes
      setUserInput('');
      setDocumentFile(null);
      setExtractedText('');
      setShowProtocolForm(false);
      setSteps([]);
      setReagents([]);
    }
  }, [isOpen, fetchProjectInfo, prefillTitle, prefillDescription]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (fileType, fileName) => {
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return 'bi-file-earmark-pdf';
    } else if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
      return 'bi-file-earmark-text';
    }
    return 'bi-file-earmark';
  };

  const extractTextFromPDF = async (file) => {
    const pdfjs = window.pdfjsLib || window.pdfjs;
    
    if (!pdfjs) {
      throw new Error('PDF.js library not loaded. Please refresh the page and try again.');
    }
    
    if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }

    setLoadingMessage('Reading PDF file...');
    setIsProcessingDocument(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      setLoadingMessage('Loading PDF document...');
      
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdf.numPages;
      
      let extractedText = '';
      
      for (let i = 1; i <= totalPages; i++) {
        setLoadingMessage(`Extracting text from page ${i} of ${totalPages}...`);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        let textItems = [];
        for (const item of textContent.items) {
          textItems.push({
            text: item.str,
            x: item.transform[4],
            y: item.transform[5],
            fontSize: item.fontSize || 10
          });
        }
        
        textItems.sort((a, b) => {
          const yDiff = Math.abs(a.y - b.y);
          if (yDiff < 5) {
            return a.x - b.x;
          }
          return b.y - a.y;
        });
        
        let currentY = -1;
        let pageText = '';
        
        for (const item of textItems) {
          if (Math.abs(currentY - item.y) > 5) {
            if (currentY !== -1) pageText += '\n';
            currentY = item.y;
          } else if (item.text.trim() !== '') {
            pageText += ' ';
          }
          pageText += item.text;
        }
        
        extractedText += pageText + '\n\n';
      }
      
      setIsProcessingDocument(false);
      return cleanExtractedText(extractedText);
    } catch (error) {
      setIsProcessingDocument(false);
      throw new Error('Failed to extract text from PDF: ' + error.message);
    }
  };

  const readTextFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  const cleanExtractedText = (text) => {
    let cleaned = text.replace(/\s+/g, ' ');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    // eslint-disable-next-line no-control-regex
    cleaned = cleaned.replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F]/g, '');
    return cleaned.trim();
  };

  const processDocumentFile = async (file) => {
    try {
      setIsProcessingDocument(true);
      setShowLoadingModal(true);
      setLoadingMessage('Processing document...');
      
      let text = '';
      
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        text = await extractTextFromPDF(file);
      } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        text = await readTextFile(file);
      } else {
        throw new Error('Unsupported file type. Please upload PDF or TXT files.');
      }
      
      const previewLength = 1000;
      setUserInput(text.length > previewLength ? text.substring(0, previewLength) + '...' : text);
      setExtractedText(text);
      setDocumentFile(file);
      
      setIsProcessingDocument(false);
      setShowLoadingModal(false);
      return text;
    } catch (error) {
      setIsProcessingDocument(false);
      setShowLoadingModal(false);
      setErrorMessage(error.message);
      setShowErrorModal(true);
      throw error;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    const validExtensions = ['.pdf'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setErrorMessage('Please upload a PDF file.');
      setShowErrorModal(true);
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('File size must be less than 10MB.');
      setShowErrorModal(true);
      return;
    }
    
    try {
      await processDocumentFile(file);
      if (fileInfoRef.current) {
        fileInfoRef.current.style.display = 'flex';
      }
    } catch (error) {
      console.error('Error processing file:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('dragover');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const removeFile = () => {
    setDocumentFile(null);
    setExtractedText('');
    setUserInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (fileInfoRef.current) {
      fileInfoRef.current.style.display = 'none';
    }
  };

  const generateProtocol = async () => {
    let inputText = userInput.trim();
    
    if (extractedText && documentFile) {
      inputText = extractedText;
    }
    
    if (!inputText) {
      setErrorMessage('Please enter text or upload a document');
      setShowErrorModal(true);
      return;
    }
    
    setIsGenerating(true);
    setShowLoadingModal(true);
    setLoadingMessage('Generating Protocol...');
    setLoadingSubtext('Our AI is analyzing your document and creating a protocol. This may take a minute.');
    
    try {
      const response = await API.post('/qurator/ai/generate', {
        userInput: inputText
      });
      
      if (response.data.error) {
        throw new Error(response.data.message || 'Failed to generate protocol');
      }
      
      const data = response.data;
      
      setFormData({
        draft_title: data.title || '',
        protocol_description: data.description || ''
      });
      
      setSteps(data.protocolsteps || []);
      setReagents((data.reagentlist || []).map(r => ({ name: r, vendor: '', lot: '', serial: '' })));
      
      setShowProtocolForm(true);
      setShowLoadingModal(false);
    } catch (error) {
      console.error('Error generating protocol:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to generate protocol. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsGenerating(false);
      setShowLoadingModal(false);
    }
  };

  const handleUseGenerated = () => {
    // Pass generated data back to parent (Drafts page)
    if (onProtocolGenerated) {
      onProtocolGenerated({
        draft_title: formData.draft_title,
        protocol_description: formData.protocol_description,
        steps: steps.filter(s => s && s.trim()),
        reagents: reagents.filter(r => r.name && r.name.trim()).map(r => ({
          name: r.name,
          vendor: r.vendor || '',
          lot: r.lot || '',
          serial: r.serial || ''
        }))
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="ai-modal-overlay" onClick={onClose}>
        <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="ai-modal-header">
            <h4>AI-Assisted Protocol Creation</h4>
            <p>Let our AI help you generate protocols from documents or text</p>
            <div className="project-info">
              <p>Project: {projectName}</p>
            </div>
            <button className="ai-modal-close" onClick={onClose}>×</button>
          </div>

          <div className="ai-modal-body">
            {/* Manual Creation Card */}
            <div className="manual-assist-card">
              <div className="manual-content">
                <div className="manual-icon">
                  <i className="bi bi-pencil-square"></i>
                </div>
                <div className="manual-info">
                  <h5>Prefer manual creation?</h5>
                  <p>Create your simulation step by step with our intuitive manual editor</p>
                </div>
              </div>
              <button 
                type="button"
                className="manual-btn"
                onClick={onClose}
              >
                Switch to Manual
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>

            <form id="aiForm">
              {/* AI Input Area */}
              <div className="form-section">
                <div className="section-title">
                  <i className="bi bi-robot"></i>
                  <h5>Input Your Content</h5>
                </div>
                
                <div className="alert alert-info">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  Paste protocol text below or upload a PDF, Word, or TXT document. Our AI will analyze it and generate a structured protocol.
                </div>
                
                {/* Document Upload Area */}
                <div 
                  id="uploadArea" 
                  className="upload-area"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <i className="bi bi-file-earmark-pdf upload-icon"></i>
                  <h5>Drag & Drop your document here</h5>
                  <p className="text-muted">Supported formats: PDF</p>
                  <input 
                    type="file" 
                    id="documentInput" 
                    ref={fileInputRef}
                    accept=".pdf" 
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <button 
                    type="button" 
                    id="browseButton" 
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select Document
                  </button>
                </div>
                
                <div id="fileInfo" ref={fileInfoRef} className="file-info" style={{ display: 'none' }}>
                  <i className={`bi ${documentFile ? getFileTypeIcon(documentFile.type, documentFile.name) : 'bi-file-earmark'} file-icon`}></i>
                  <div>
                    <h6 className="mb-1">{documentFile?.name || 'document.pdf'}</h6>
                    <small className="text-muted">
                      Size: {documentFile ? formatFileSize(documentFile.size) : '1.2 MB'}
                    </small>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-danger ms-auto" 
                    onClick={removeFile}
                  >
                    <i className="bi bi-x"></i> Remove
                  </button>
                </div>
                
                {/* Text Input */}
                <div className="mb-4">
                  <textarea 
                    id="user-input" 
                    className="form-control" 
                    rows="6" 
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Paste your protocol, methods section, or research paper here..."
                  />
                </div>
                
                <div className="d-flex align-items-center">
                  <button 
                    type="button" 
                    className="btn-generate" 
                    onClick={generateProtocol}
                    disabled={isGenerating || isProcessingDocument}
                  >
                    <i className="bi bi-magic"></i>
                    Generate Protocol
                    {isGenerating && (
                      <div className="spinner ms-2"></div>
                    )}
                  </button>
                </div>
              </div>

              {/* Generated Content Form */}
              {showProtocolForm && (
                <div id="protocolForm">
                  {/* Basic Information */}
                  <div className="form-section">
                    <div className="section-title">
                      <i className="bi bi-info-circle"></i>
                      <h5>Basic Information</h5>
                    </div>
                    <div className="input-area">
                      <div className="mb-3">
                        <label className="form-label required-field">Protocol Title</label>
                        <input 
                          type="text" 
                          name="draft_title" 
                          className="form-control" 
                          value={formData.draft_title}
                          onChange={(e) => setFormData({ ...formData, draft_title: e.target.value })}
                          required 
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label required-field">Description</label>
                        <textarea 
                          name="protocol_description" 
                          className="form-control" 
                          rows="3" 
                          value={formData.protocol_description}
                          onChange={(e) => setFormData({ ...formData, protocol_description: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Steps Section */}
                    <div className="col-md-6">
                      <div className="form-section">
                        <div className="section-title">
                          <i className="bi bi-list-ol"></i>
                          <h5>Protocol Steps</h5>
                          <span className="text-danger">*</span>
                        </div>
                        <div id="stepsContainer" className="input-area">
                          {steps.map((step, index) => (
                            <div key={index} className="input-item">
                              <div className="d-flex align-items-start gap-2">
                                <div className="flex-grow-1">
                                  <label className="form-label small">Step {index + 1}</label>
                                  <textarea 
                                    className="form-control" 
                                    rows="2" 
                                    value={step}
                                    onChange={(e) => {
                                      const newSteps = [...steps];
                                      newSteps[index] = e.target.value;
                                      setSteps(newSteps);
                                    }}
                                    required
                                  />
                                </div>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-danger btn-sm" 
                                  onClick={() => setSteps(steps.filter((_, i) => i !== index))}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button 
                          type="button" 
                          className="btn btn-outline-primary w-100 mt-3" 
                          onClick={() => setSteps([...steps, ''])}
                        >
                          <i className="bi bi-plus-lg"></i> Add Step
                        </button>
                      </div>
                    </div>

                    {/* Reagents Section */}
                    <div className="col-md-6">
                      <div className="form-section">
                        <div className="section-title">
                          <i className="bi bi-flask"></i>
                          <h5>Reagents</h5>
                          <span className="text-danger">*</span>
                        </div>
                        <div id="reagentsContainer" className="input-area">
                          {reagents.map((reagent, index) => (
                            <div key={index} className="input-item">
                              <div className="d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <label className="form-label small mb-0">Reagent {index + 1}</label>
                                  <button 
                                    type="button" 
                                    className="btn btn-outline-danger btn-sm" 
                                    onClick={() => setReagents(reagents.filter((_, i) => i !== index))}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                                <input 
                                  type="text" 
                                  className="form-control mb-2" 
                                  value={reagent.name}
                                  onChange={(e) => {
                                    const newReagents = [...reagents];
                                    newReagents[index] = { ...newReagents[index], name: e.target.value };
                                    setReagents(newReagents);
                                  }}
                                  placeholder="Enter reagent name" 
                                  required
                                />
                                <div className="row g-2">
                                  <div className="col-md-4">
                                    <input 
                                      type="text" 
                                      className="form-control form-control-sm" 
                                      value={reagent.vendor || ''}
                                      onChange={(e) => {
                                        const newReagents = [...reagents];
                                        newReagents[index] = { ...newReagents[index], vendor: e.target.value };
                                        setReagents(newReagents);
                                      }}
                                      placeholder="Vendor (Optional)"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <input 
                                      type="text" 
                                      className="form-control form-control-sm" 
                                      value={reagent.lot || ''}
                                      onChange={(e) => {
                                        const newReagents = [...reagents];
                                        newReagents[index] = { ...newReagents[index], lot: e.target.value };
                                        setReagents(newReagents);
                                      }}
                                      placeholder="Lot # (Optional)"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <input 
                                      type="text" 
                                      className="form-control form-control-sm" 
                                      value={reagent.serial || ''}
                                      onChange={(e) => {
                                        const newReagents = [...reagents];
                                        newReagents[index] = { ...newReagents[index], serial: e.target.value };
                                        setReagents(newReagents);
                                      }}
                                      placeholder="Serial # (Optional)"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button 
                          type="button" 
                          className="btn btn-outline-primary w-100 mt-3" 
                          onClick={() => setReagents([...reagents, { name: '', vendor: '', lot: '', serial: '' }])}
                        >
                          <i className="bi bi-plus-lg"></i> Add Reagent
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Use Generated Button */}
                  <div className="text-end mt-4 mb-4">
                    <button 
                      type="button" 
                      className="btn-generate" 
                      onClick={handleUseGenerated}
                    >
                      <i className="bi bi-check-lg"></i>
                      Use This Protocol
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="modal-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body text-center">
              <i className="bi bi-x-circle-fill error-icon"></i>
              <h5 className="mb-3">Error Occurred</h5>
              <p className="mb-4">{errorMessage}</p>
              <button 
                type="button" 
                className="btn btn-confirm px-4" 
                onClick={() => setShowErrorModal(false)}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {showLoadingModal && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-body text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="mb-0">{loadingMessage}</h5>
              <p className="text-muted mt-2">{loadingSubtext}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistantModal;
